import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import classNames from 'classnames/bind'
import { useLocation } from '@reach/router'

import WistiaControls from './WistiaControls'

import s from './WistiaEmbed.module.scss'
const cn = classNames.bind(s)

const defaultOptions = {
  // PLAYBACK
  preload: true,
  muted: false,
  playsinline: true,
  autoPlay: false,
  silentAutoPlay: false,
  endVideoBehavior: 'default',
  fakeFullscreen: false,

  // LAYOUT
  fitStrategy: 'contain',
  videoFoam: false,
  // wmode: 'transparent',

  // TRACKING
  copyLinkAndThumbnailEnabled: false,
  doNotTrack: true,
  googleAnalytics: false,
  seo: false,

  // QUALITY
  // qualityMax,
  // qualityMin,

  // UI CONTROLS
  controlsVisibleOnLoad: false,
  fullscreenOnRotateToLandscape: false,
  fullscreenButton: false,
  settingsControl: false,
  qualityControl: false,
  playButton: false,
  playbar: false,
  playbackRateControl: false,
  smallPlayButton: false,
}

const play = (el) => {
  if (typeof el.play !== 'function') return
  return el.play()
}

const pause = (el) => {
  if (typeof el.pause !== 'function') return
  return el.pause()
}

const WistiaEmbed = ({
  id,
  controls = true,
  options: _options,
  style,
  initialState,
  readyCallback = null,
  remountOnRouteChange = true,
}) => {
  const { key: locationKey } = useLocation()
  const routeKey = remountOnRouteChange ? locationKey : id
  const [isReady, setIsReady] = useState(false)
  const [embedNonce, setEmbedNonce] = useState(0)
  const videoRef = useRef(null)
  const wrapperRef = useRef(null)
  const instanceIdRef = useRef(0)

  const options = { ...defaultOptions, ..._options }

  useEffect(() => {
    ++instanceIdRef.current
    setIsReady(false)
    setEmbedNonce((n) => n + 1)

    return () => {
      if (videoRef.current?.remove) {
        videoRef.current.remove()
      }
      videoRef.current = null
      setIsReady(false)
    }
  }, [id, routeKey])

  useEffect(() => {
    if (!embedNonce) return

    const instanceId = instanceIdRef.current

    const onReady = (video) => {
      if (instanceId !== instanceIdRef.current) {
        return
      }
      videoRef.current = video
      setIsReady(true)
      readyCallback?.(video)
    }

    // Push — do not spread _wq; copying the queue on every mount/HMR balloons memory
    window._wq = window._wq || []
    if (!window.__wistiaConsentInitialized) {
      window._wq.push((w) => w.consent(false))
      window.__wistiaConsentInitialized = true
    }
    window._wq.push({ id, options, onReady })
  }, [id, routeKey, embedNonce])

  useEffect(() => {
    if (!videoRef.current || !isReady) return
    const state = videoRef.current.state()
    if (!initialState || state === initialState) return
    if (initialState === 'playing') play(videoRef.current)
    if (initialState === 'paused') pause(videoRef.current)
  }, [isReady, initialState, videoRef.current])

  // We get weird errors when rendering from the server - let's skip
  const isSSR = typeof window === 'undefined'
  if (isSSR) return null

  return (
    <>
      <Helmet>
        <script src={`https://fast.wistia.com/embed/medias/${id}.jsonp`} async></script>
        <script src="https://fast.wistia.com/assets/external/E-v1.js" async></script>
      </Helmet>
      <div className={s.wrapper} style={style} ref={wrapperRef}>
        <div key={embedNonce} className={cn('WistiaEmbed', `wistia_embed wistia_async_${id}`)} />
        {controls && (
          <WistiaControls video={videoRef.current} wrapperRef={wrapperRef} initialStateMuted={options.silentAutoPlay} />
        )}
      </div>
    </>
  )
}

WistiaEmbed.propTypes = {
  id: PropTypes.string,
  controls: PropTypes.bool,
  autoPlay: PropTypes.bool,
  options: PropTypes.object,
  style: PropTypes.object,
  initialState: PropTypes.oneOf(['playing', 'paused']),
  readyCallback: PropTypes.func,
  remountOnRouteChange: PropTypes.bool,
}

export default WistiaEmbed
